import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
  SupervisorOrderDetails,
  fetchSupervisorOrderDetail,
  approveOrRejectOrder,
} from './services/supervisorApprovalAPI';
import styles from './SupervisorOrderDetailsPage.module.css';

const SIZES = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];

const SupervisorOrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<SupervisorOrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; status: 0 | 2 | null }>({
    open: false,
    status: null,
  });

  const orderId = Number(id);

  const loadDetails = async () => {
    if (!orderId || Number.isNaN(orderId)) return;
    try {
      setLoading(true);
      setError(null);
      const response = await fetchSupervisorOrderDetail(orderId);
      setOrder(response.data);
    } catch (e) {
      console.error(e);
      setError('Erro ao carregar detalhes do pedido.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
  }, [id]);

  const openConfirmDialog = (status: 0 | 2) => {
    setConfirmDialog({ open: true, status });
  };

  const executeUpdateStatus = async () => {
    if (!orderId || confirmDialog.status === null) return;
    try {
      setConfirmDialog((prev) => ({ ...prev, open: false }));
      setUpdating(true);
      await approveOrRejectOrder(orderId, confirmDialog.status);
      await loadDetails();
    } catch (e) {
      console.error(e);
      alert('Erro ao atualizar status do pedido.');
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (value?: number | string) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '--';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  };

  const formatCNPJ = (val?: string | null) => {
    if (!val) return '--';
    const cleaned = val.replace(/\D/g, '');
    if (cleaned.length === 14) return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (cleaned.length === 11) return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return val;
  };

  const formatPhone = (val?: string | null) => {
    if (!val) return '--';
    const cleaned = val.replace(/\D/g, '');
    if (cleaned.length === 11) return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    if (cleaned.length === 10) return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    return val;
  };

  const calculatedTotal = useMemo(() => {
    return (order?.itens || []).reduce((acc: number, item) => acc + (item.subtotal || item.quantidade * item.preco_item || 0), 0);
  }, [order]);

  const getStatusBadgeClass = (pedido_situacao: number | null) => {
    if (pedido_situacao === 3) return styles.statusDone;
    if (pedido_situacao === 2) return styles.statusCanceled;
    if (pedido_situacao === 1) return styles.statusOpen;
    return styles.statusOpen;
  };

  const getStatusLabel = (pedido_situacao: number | null) => {
    switch (pedido_situacao) {
      case 0: return 'Aguardando Supervisor';
      case 1: return 'Aguardando Amostras';
      case 2: return 'Recusado';
      case 3: return 'Finalizado';
      default: return 'Pendente';
    }
  };

  const isPending = order?.pedido_situacao === 0;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <button className={styles.backButton} onClick={() => navigate('/aprovacao-pedidos')}>
          <ArrowBackIcon fontSize="small" /> Voltar
        </button>
        <h1>Detalhes do Pedido #{order?.id || id}</h1>
      </div>

      <div className={styles.detailsCard}>
        <div className={styles.detailsHeader}>
          <div>
            <h3>Detalhes completos</h3>
          </div>
          <div className={styles.headerActionsGroup}>
            {order && (
              <span className={`${styles.statusBadge} ${getStatusBadgeClass(order.pedido_situacao)}`}>
                {getStatusLabel(order.pedido_situacao)}
              </span>
            )}
            {/* Se finalizado (homologação aprovada): link para avaliação em etapas */}
            {order?.pedido_situacao === 3 && (
              <button
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff',
                  fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem',
                }}
                onClick={() => navigate(`/amostras-pedidos/${order.id}/avaliacao`)}
              >
                <AssignmentIcon fontSize="small" /> Avaliação em Etapas
              </button>
            )}
            {isPending && (
              <div className={styles.approvalButtons}>
                <button
                  className={styles.btnReject}
                  onClick={() => openConfirmDialog(2)}
                  disabled={updating}
                >
                  Recusar
                </button>
                <button
                  className={styles.btnApprove}
                  onClick={() => openConfirmDialog(0)}
                  disabled={updating}
                >
                  Aprovar
                </button>
              </div>
            )}
          </div>
        </div>

        {loading && <p className={styles.feedback}>Carregando detalhes...</p>}
        {error && <p className={styles.feedback} style={{ color: '#b91c1c' }}>{error}</p>}
        {!loading && !order && !error && <p className={styles.feedback}>Pedido não encontrado.</p>}

        {order && (
          <div className={styles.detailsGrid}>
            {/* Coluna Esquerda */}
            <div className={styles.colLeft}>
              {/* Cliente & Vendedor */}
              <article className={styles.infoBlock}>
                <h4>
                  <PersonOutlineIcon fontSize="small" />
                  Cliente & Vendedor
                </h4>
                <div className={styles.infoBlockContent}>
                  <p className={styles.fullWidth}><span>Razão social</span><strong>{order.cliente_razao_social || '--'}</strong></p>
                  <p><span>CNPJ</span><strong>{formatCNPJ(order.cliente_cnpj)}</strong></p>
                  <p><span>Cidade/UF</span><strong>{order.cliente_cidade || '--'}/{order.cliente_estado || '--'}</strong></p>
                  <p><span>Segmento</span><strong>{order.SEGMENTO_CLASSE || '--'}</strong></p>
                  <p><span>Telefone</span><strong>{formatPhone(order.cliente_telefone)}</strong></p>
                  <p><span>E-mail</span><strong>{order.CLIENTE_EMAIL || '--'}</strong></p>
                  <p><span>Vendedor</span><strong>{order.VENDEDOR_NOME || '--'}</strong></p>
                  <p><span>Suporte</span><strong>{order.BASE_PRODUCAO_NOME || '--'}</strong></p>
                </div>
              </article>

              {/* Pedido */}
              <article className={styles.infoBlock}>
                <h4>
                  <ReceiptLongOutlinedIcon fontSize="small" />
                  Pedido
                </h4>
                <div className={styles.infoBlockContent}>
                  <p><span>Data emissão</span><strong>{formatDate(order.data_emissao)}</strong></p>
                  <p><span>Operação fiscal</span><strong>{order.operacao_fiscal_desc ?? order.operacoes_fiscais ?? '--'}</strong></p>
                  <p><span>Cond. pagamento</span><strong>{order.condicao_pagamento || '--'}</strong></p>
                  <p><span>Total</span><strong>{formatCurrency(calculatedTotal)}</strong></p>
                </div>

                {/* Itens */}
                <h4 className={styles.subSectionTitle} style={{ marginTop: '20px' }}>
                  <Inventory2OutlinedIcon fontSize="small" />
                  Itens do Pedido
                </h4>
                {(!order.itens || order.itens.length === 0) && (
                  <p><span>Itens</span><strong>Nenhum item vinculado.</strong></p>
                )}
                {order.itens && order.itens.length > 0 && (
                  <div className={styles.itemsList}>
                    {order.itens.map((item) => (
                      <div key={item.id} className={styles.productRowHorizontal}>
                        <div className={styles.productIdCol}>
                          <span className={styles.productRefMini}>{item.referencia}</span>
                          <span className={styles.productDescCompact}>{item.descricao || 'Item sem descrição'}</span>
                        </div>
                        <div className={styles.productQtdCol}>
                          <div className={styles.miniFinItem}>
                            <span>Qtd</span>
                            <strong>{item.quantidade}</strong>
                          </div>
                        </div>
                        <div className={styles.productGradeCol}>
                          <div className={styles.gradeGridMini}>
                            {SIZES.map((size) => {
                              const g = item.grades?.find((gr) => Number(gr.tamanho) === size);
                              const qty = g ? g.quantidade : 0;
                              return (
                                <div key={size} className={`${styles.miniGradeBox} ${qty === 0 ? styles.gradeHiddenMobile : ''}`}>
                                  <span className={styles.miniGradeSize}>{size}</span>
                                  <span className={styles.miniGradeQty} style={qty === 0 ? { color: '#cbd5e1' } : undefined}>{qty || '-'}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className={styles.productTotalCol}>
                          <div className={styles.miniFinItem}>
                            <span>Subtotal</span>
                            <strong>{formatCurrency(item.subtotal ?? item.quantidade * item.preco_item)}</strong>
                          </div>
                          <div className={styles.miniFinItem}>
                            <span>Preço un.</span>
                            <strong>{formatCurrency(item.preco_item)}</strong>
                          </div>
                          <div className={styles.miniFinItem}>
                            <span>Tabela</span>
                            <strong>{item.tabela_preco}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>

              {/* Observações */}
              {order.obs && (
                <article className={styles.infoBlock}>
                  <h4>Observação</h4>
                  <div className={styles.infoBlockContent}>
                    <p className={styles.fullWidth}><strong>{order.obs}</strong></p>
                  </div>
                </article>
              )}
            </div>

            {/* Coluna Direita */}
            <div className={styles.colRight}>
              {/* Amostra (se existir) */}
              {order.amostra && (
                <article className={styles.infoBlock}>
                  <h4>
                    <ScienceOutlinedIcon fontSize="small" />
                    Dados da Amostra
                  </h4>
                  <div className={styles.infoBlockContent}>
                    <p><span>Possui Filial</span><strong>{order.amostra.possui_filial || '--'}</strong></p>
                    <p><span>Unidade Compra</span><strong>{order.amostra.unidade_compra || '--'}</strong></p>
                    <p><span>N° Usuários</span><strong>{order.amostra.num_usuarios || '--'}</strong></p>
                    <p><span>Contato</span><strong>{order.amostra.contato_principal || '--'}</strong></p>
                    <p><span>Telefone</span><strong>{formatPhone(order.amostra.telefone_contato)}</strong></p>
                    <p><span>Email</span><strong>{order.amostra.email_contato || '--'}</strong></p>
                    <p><span>Marca Atual</span><strong>{order.amostra.ca_marca_atual || '--'}</strong></p>
                    <p><span>Riscos</span><strong>{order.amostra.riscos_area || '--'}</strong></p>
                    <p><span>Insatisfação</span><strong>{order.amostra.insatisfacao_atual || '--'}</strong></p>
                    <p><span>Calçado Sugerido</span><strong>{order.amostra.calcado_sugerido || '--'}</strong></p>
                    {order.amostra.observacoes && (
                      <p className={styles.fullWidth}><span>Observações</span><strong>{order.amostra.observacoes}</strong></p>
                    )}
                  </div>
                </article>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Diálogo de confirmação */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, status: null })}>
        <DialogTitle>
          {confirmDialog.status === 0 ? 'Aprovar Pedido' : 'Recusar Pedido'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.status === 0
              ? 'Deseja aprovar este pedido? Se for homologação, uma ficha de avaliação será criada automaticamente.'
              : 'Deseja realmente recusar este pedido?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, status: null })}>Cancelar</Button>
          <Button
            onClick={executeUpdateStatus}
            color={confirmDialog.status === 0 ? 'success' : 'error'}
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SupervisorOrderDetailsPage;
